import React, { useEffect, useRef, useState } from "react";

const DURATION = 2600;
const PAD = 120;

// Point on a rounded-rectangle perimeter at fraction t (0..1), clockwise from top-left.
const perimeterPoint = (t, w, h, r) => {
  const straight = [w - 2 * r, h - 2 * r, w - 2 * r, h - 2 * r];
  const arc = (Math.PI / 2) * r;
  const total = straight.reduce((a, b) => a + b, 0) + 4 * arc;
  let d = t * total;
  const corners = [[w - r, r, -Math.PI / 2], [w - r, h - r, 0], [r, h - r, Math.PI / 2], [r, r, Math.PI]];
  const starts = [[r, 0, 1, 0], [w, r, 0, 1], [w - r, h, -1, 0], [0, h - r, 0, -1]];
  for (let i = 0; i < 4; i++) {
    if (d <= straight[i]) { const [x, y, dx, dy] = starts[i]; return [x + dx * d, y + dy * d]; }
    d -= straight[i];
    if (d <= arc) { const [cx, cy, a0] = corners[i]; const a = a0 + d / r; return [cx + r * Math.cos(a), cy + r * Math.sin(a)]; }
    d -= arc;
  }
  return [r, 0];
};

// Gold welding sparks trace the emblem's edge once per visit, then the logo rests still.
export const WeldSparks = ({ radius = 16 }) => {
  const canvasRef = useRef(null);
  const [show, setShow] = useState(() => {
    if (sessionStorage.getItem("bsn_sparks_done")) return false;
    return !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  useEffect(() => {
    if (!show) return;
    const cv = canvasRef.current;
    const holder = cv.parentElement;
    const img = holder.querySelector("img");
    let raf, started = false;

    const begin = () => {
    started = true;
    const box = holder.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const W = box.width + PAD * 2, H = box.height + PAD * 2;
    cv.width = W * dpr; cv.height = H * dpr;
    cv.style.width = `${W}px`; cv.style.height = `${H}px`;
    const ctx = cv.getContext("2d");
    ctx.scale(dpr, dpr);
    const w = box.width, h = box.height;
    const sparks = [];
    const trail = [];
    let start = null, last = 0;

    const frame = (now) => {
      if (start === null) { start = now; last = now; }
      const el = now - start, dt = Math.min(40, now - last) / 16.7;
      last = now;
      const t = Math.min(1, el / DURATION);
      ctx.clearRect(0, 0, W, H);

      if (t < 1) {
        const [x, y] = perimeterPoint(t, w, h, radius);
        trail.push([x + PAD, y + PAD]);
        for (let i = 0; i < 12; i++) {
          const a = Math.random() * Math.PI * 2, s = 2.5 + Math.random() * 4.5;
          sparks.push({ x: x + PAD, y: y + PAD, vx: Math.cos(a) * s, vy: Math.sin(a) * s - 1.8, life: 1, decay: 0.016 + Math.random() * 0.024, w: 2 + Math.random() * 1.8 });
        }
      }

      const fade = t < 1 ? 1 : Math.max(0, 1 - (el - DURATION) / 900);
      if (trail.length > 1) {
        ctx.save();
        ctx.globalAlpha = 0.85 * fade;
        ctx.strokeStyle = "#F5D77F";
        ctx.lineWidth = 3.5;
        ctx.shadowColor = "#D4AF37";
        ctx.shadowBlur = 18;
        ctx.beginPath();
        trail.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
        ctx.stroke();
        ctx.restore();
      }

      if (t < 1) {
        const [hx, hy] = trail[trail.length - 1];
        const g = ctx.createRadialGradient(hx, hy, 0, hx, hy, 30);
        g.addColorStop(0, "rgba(255,255,255,1)");
        g.addColorStop(0.3, "rgba(255,228,150,0.95)");
        g.addColorStop(1, "rgba(212,175,55,0)");
        ctx.fillStyle = g;
        ctx.beginPath(); ctx.arc(hx, hy, 30, 0, Math.PI * 2); ctx.fill();
      }

      for (let i = sparks.length - 1; i >= 0; i--) {
        const p = sparks[i];
        p.vy += 0.18 * dt; p.x += p.vx * dt; p.y += p.vy * dt; p.life -= p.decay * dt;
        if (p.life <= 0) { sparks.splice(i, 1); continue; }
        ctx.strokeStyle = p.life > 0.6 ? `rgba(255,250,225,${p.life})` : `rgba(240,190,70,${p.life})`;
        ctx.lineWidth = p.w;
        ctx.lineCap = "round";
        ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(p.x - p.vx * 3, p.y - p.vy * 3); ctx.stroke();
      }

      if (t >= 1 && fade <= 0 && sparks.length === 0) { sessionStorage.setItem("bsn_sparks_done", "1"); setShow(false); return; }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    };

    // Wait until the emblem image has loaded and is mostly on screen.
    let visible = false, wait;
    const tryBegin = () => { if (!started && !wait && visible && img?.complete && img.naturalWidth > 0) wait = setTimeout(begin, 250); };
    const io = new IntersectionObserver(([e]) => { visible = e.intersectionRatio >= 0.6; tryBegin(); }, { threshold: [0, 0.6, 1] });
    io.observe(holder);
    img?.addEventListener("load", tryBegin);
    return () => { clearTimeout(wait); cancelAnimationFrame(raf); io.disconnect(); img?.removeEventListener("load", tryBegin); };
  }, [show, radius]);

  if (!show) return null;
  return <canvas ref={canvasRef} aria-hidden="true" className="absolute pointer-events-none z-20" style={{ left: -PAD, top: -PAD }} data-testid="logo-weld-sparks" />;
};
